/**
 * Cache-bust static /public assets.
 * Nginx serves *.png with Cache-Control: immutable for 7d — bump ASSET_V
 * whenever icon bytes change so Telegram WebView picks up new art.
 *
 * Every icon also ships as WebP (see `frontend/tools/optimize-icons.py`), sized
 * for the place the UI paints it. `iconUrl` still returns the PNG so it stays
 * safe inside CSS `url()` and as the <picture> fallback; `iconWebp` returns the
 * cheap variant, which is what <img> elements should render.
 */
import manifest from './iconManifest.json';

export const ASSET_V = '20260817a';

/** `coin.png`, `/assets/icons/coin.png`, `/assets/icons/coin.png?v=1` → `coin.png`. */
function iconName(file) {
  return String(file || '')
    .split('?')[0]
    .replace(/^\/?assets\/icons\//, '');
}

export function iconUrl(file) {
  const name = iconName(file);
  if (!name) return `/assets/icons/toolbox.png?v=${ASSET_V}`;
  return `/assets/icons/${name}?v=${ASSET_V}`;
}

/**
 * WebP variant of an icon, or null when the optimizer produced none.
 *
 * `still: true` picks the single first frame of an animated sticker — the icon
 * looks the same standing still, but the browser stops running a 90-frame
 * decode loop for something the design shows idle.
 * `small: true` picks the thumbnail cut of large cosmetic art.
 */
export function iconWebp(file, { still = false, small = false } = {}) {
  const name = iconName(file);
  const entry = manifest[name];
  if (!entry) return null;
  const stem = name.replace(/\.png$/i, '');
  if (small && entry.sm) return `/assets/icons/${stem}.sm.webp?v=${ASSET_V}`;
  if (still && entry.still) return `/assets/icons/${stem}.still.webp?v=${ASSET_V}`;
  return `/assets/icons/${stem}.webp?v=${ASSET_V}`;
}

/** True when the icon is a multi-frame sticker that costs decode work while visible. */
export function iconIsAnimated(file) {
  return Boolean(manifest[iconName(file)]?.anim);
}

/**
 * The flattened background plate for a planet atmosphere (or the plain screen).
 *
 * Each one collapses dust + vignette + colour grade + the skin's veil and film
 * grain into a single source-over layer — see frontend/tools/bake-bg-overlay.py.
 */
export function bgOverlayUrl(atmosphere) {
  const name = atmosphere ? `bg-overlay-${atmosphere}` : 'bg-overlay';
  return `/assets/media/${name}.webp?v=${ASSET_V}`;
}
